import { Injectable, inject } from '@angular/core';
import { BranchDeepLinks, BranchInitEvent, BranchReferringParams } from 'capacitor-branch-deep-links';
import { BaseContainer } from '@app/shared/abstract/base.container';
import { take, finalize } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { Router } from '@angular/router';
import { LoadingController, Platform } from '@ionic/angular';
import { CampaignSandbox } from '../sandboxes/campaign.sandbox';
import { Capacitor } from '@capacitor/core';
import { InvitationService } from './invitation.service';
import { RegistrationDataService } from './registration-data.service';
import { DeepLinkFeatures } from '@app/core/enums/deep-link.enum';
import { RegistrationData } from '@app/core/types/registration-data.types';

@Injectable({ providedIn: 'root' })
export class DeepLinkService extends BaseContainer {
  loadingController = inject(LoadingController);
  private readonly invitationService = inject(InvitationService);
  private readonly registrationDataService = inject(RegistrationDataService);
  private readonly platform = inject(Platform);

  loader: HTMLIonLoadingElement;

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private campaignSandbox: CampaignSandbox
  ) {
    super();
  }

  init() {
    if (Capacitor.isNativePlatform()) {
      BranchDeepLinks.addListener('init', ({ referringParams }: BranchInitEvent) => {

        if (Object.keys(referringParams).length === 0) {
          return;
        }

        if (referringParams['+clicked_branch_link'] === false) {
          return;
        }

        this.startLoader().then(() => {
          this.authService.authenticated$
            .pipe(
              take(1),
              finalize(() => this.dismissLoader())
            )
            .subscribe((authenticated: boolean) => {
              if (referringParams['~feature'] === DeepLinkFeatures.UserInvitation) {
                this.handleInvitationDeeplink(referringParams['email'], referringParams['invitationId'], referringParams['verificationCode'], authenticated);
              }

              if (referringParams['~feature'] === DeepLinkFeatures.OnboardingPrefill) {
                this.handleOnboardingPrefillDeeplink(referringParams, authenticated);
              }

              if (referringParams['campaignId']) {
                this.handleCampaignDeeplink(referringParams, authenticated);
              }
            });
        });
      });

      BranchDeepLinks.addListener('initError', (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
    }
  }

  async startLoader(): Promise<void> {
    this.loader = await this.loadingController.create();
    this.loader.present();
  }

  dismissLoader(): void {
    this.loader.dismiss();
  }

  async logBranchEvent(eventName: string, metaData: any = {}): Promise<void> {
    if (!this.platform.is('capacitor')) {
      return;
    }

    await BranchDeepLinks.sendBranchEvent({ eventName, metaData });
  }

  private handleInvitationDeeplink(email: string, invitationId: string, invitationCode: string, authenticated: boolean): void {
    if (invitationCode) {
      this.invitationService.invitationCode = invitationCode;
    }
    
    if (!authenticated) {
      this.authService.register(email, invitationId);
    } else {
      this.invitationService.prefillRegistrationData(invitationId).subscribe();
    }
  }

  private handleOnboardingPrefillDeeplink(referringParams: BranchReferringParams, authenticated: boolean): void {
    if (authenticated) {
      return;
    }

    const prefill = this.toRegistrationData(referringParams);
    // Nothing usable in the link: fall back to the regular login/registration flow.
    if (Object.keys(prefill).length === 0) {
      return;
    }

    this.registrationDataService.setRegistrationData(prefill);
    this.authService.register(prefill.email);
  }

  private toRegistrationData(referringParams: BranchReferringParams): RegistrationData {
    const keys: (keyof RegistrationData)[] = ['firstName', 'lastName', 'email', 'phoneNumber'];

    return keys.reduce((data: RegistrationData, key) => {
      const value = referringParams[key];
      if (typeof value === 'string' && value.trim()) {
        data[key] = value.trim();
      }
      return data;
    }, {});
  }

  private handleCampaignDeeplink(referringParams: BranchReferringParams, authenticated: boolean): void {
    this.campaignSandbox.checkCampaign(referringParams, authenticated).subscribe((applicationRoute: string | null) => {
      if (applicationRoute) {
        this.router.navigateByUrl(applicationRoute, { replaceUrl: true });
      }
    });
  }
}
